import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { useDraftPersistence } from '@eureca/ui/forms';
import { arrayMove } from '@dnd-kit/sortable';
import type { GetJobsId200, PutJobsIdStepsMutationRequest } from '@eureca/recruiter-api-sdk/types';
import {
  JobFlowPage as JobFlowPageComponent,
  type FatorHCardStatus,
  PHASE_TYPE_OPTIONS,
  type JobPhase,
  type JobStage,
  type PhaseAction,
  type PhaseTypeBadge,
  type StageAction,
  type StageBadgeType,
} from './JobFlowPage.component';
import { emptyPhaseDraft, emptySheetValues } from './JobFlowPage.mock';
import { uploadJobContentAsset } from './content-asset-upload';
import { JobFlowLoadingFallback } from './JobFlowLoadingFallback';
import { JobFlowErrorFallback } from './JobFlowErrorFallback';
import type { PhaseStageDraft, SheetSelectOption } from './PhaseSheet';
import type { PhaseDraft } from './PhaseAddDialog';
import { JobFetchError, jobByIdQueryOptions } from '../job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '../job-shared/job-status';
import { JobMutationError, usePutJobSteps } from '../job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '../jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '../job-shared/use-job-company-logo';
import {
  fatorHCatalogueQueryOptions,
  formsCatalogueQueryOptions,
  eligibleTestsQueryOptions,
  kolabInstancesQueryOptions,
} from './catalogues-query';
import { computeResubmitLock } from './resubmit-lock';
import { FatorHSyncSheet } from './FatorHSyncSheet';
import { fatorHSyncCandidatesQueryOptions } from './fator-h-sync-query';
import { useExportStageResponses } from './use-export-stage-responses';
import { StageDetailsSheet } from './StageDetailsSheet';

// `ApiPhase` describes the READ shape (BFF mirrors Core's rich row which
// still exposes `phases[].stages[]` synthesized server-side from
// `job_step + module + form_module`). Sourced from the GET response so
// read-only fields (`enrolledCount`, `completedCount`, `answerCount`) são
// visíveis no tipo. The WRITE path uses `PutJobsIdStepsMutationRequest`
// instead — see `toJobStepsBody` below.
type ApiPhase = GetJobsId200['phases'][number];

/**
 * Subconjunto mutável de uma `JobStage` que o editor de etapa altera.
 * Usado para diferir a aplicação da edição quando o modal "Reabrir etapa"
 * (RN-06/07) precisa de confirmação antes de persistir.
 */
type StageEditPatch = Pick<
  JobStage,
  | 'name'
  | 'type'
  | 'startDate'
  | 'endDate'
  | 'endTime'
  | 'formId'
  | 'testId'
  | 'fatorHId'
  | 'canResubmit'
  | 'contentText'
  | 'contentFileName'
  | 'contentFileUrl'
  | 'contentFileType'
  | 'externalEmbedMode'
  | 'externalUrl'
  | 'externalPlatform'
  | 'kolabInstanceId'
  | 'rawConfig'
>;

/**
 * Hydrate component-side phase state from the API row. Sorts by `position`
 * so re-orderings persist correctly across reloads even if the BFF returns
 * out-of-order rows. The 1-based `index` is computed from sort order.
 */
const PHASE_TYPE_VALUES: readonly PhaseTypeBadge[] = [
  'trilha-online',
  'jornada-aprofundamento',
  'dinamica',
  'entrevista',
  // FAS RN-03 — write-tolerance: aceitamos `contratacao-final` na hidratação
  // de vagas legadas (Core key `hiring`) mesmo que o picker não ofereça
  // o type. Sem esse token aqui, `isPhaseTypeBadge` rejeitaria o read e a
  // fase ficaria com `type: null` no SPA.
  'contratacao-final',
];

function isPhaseTypeBadge(value: unknown): value is PhaseTypeBadge {
  return typeof value === 'string' && (PHASE_TYPE_VALUES as readonly string[]).includes(value);
}

/**
 * Janela de debounce do auto-save — mesmo sweet-spot da subseção
 * Informações gerais (EU-158) para coerência de mental model. Exposto para
 * os testes Vitest via export.
 */
export const AUTOSAVE_DEBOUNCE_MS = 500;

/**
 * Seed RN-01 — pré-criação automática das 4 fases padrão quando o consultor
 * abre o Fluxo de uma vaga que ainda não tem fases salvas. IDs começam com
 * `phase-seed-` para serem identificáveis no log; o servidor não impõe regra
 * de unicidade, então round-trip preserva.
 */
const DEFAULT_PHASES: readonly JobPhase[] = [
  {
    id: 'phase-seed-1',
    index: 1,
    name: 'Primeira Fase',
    type: 'trilha-online',
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [],
  },
  {
    id: 'phase-seed-2',
    index: 2,
    name: 'Segunda Fase',
    // SEL-001 — sequência canônica do funil: Trilha online → Vaga de
    // aprofundamento → Dinâmica → Entrevista. A 2ª fase é o segundo passo
    // do candidato (não Dinâmica). Bug reportado na auditoria de Etapas
    // de Seleção.
    type: 'jornada-aprofundamento',
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [],
  },
  {
    id: 'phase-seed-3',
    index: 3,
    name: 'Terceira Fase',
    type: 'dinamica',
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [],
  },
  {
    id: 'phase-seed-4',
    index: 4,
    name: 'Quarta Fase',
    type: 'entrevista',
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [],
  },
];

/**
 * Comparação estrutural de duas listas de fases. Usada para dedup do
 * auto-save: se a nova lista bate com a última persistida (reorder em si
 * mesmo, edição que volta ao original), NÃO disparar PATCH redundante.
 *
 * Stages são out-of-scope EU-165 mas a UI ainda permite editar/ocultar/
 * excluir/adicionar — o auto-save precisa cobrir essas mudanças, então a
 * comparação inclui TODOS os campos mutáveis da stage (sem isso um toggle
 * de `hidden` nunca seria persistido).
 */
function stagesEqual(a: JobStage, b: JobStage): boolean {
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.type === b.type &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    a.endTime === b.endTime &&
    (a.hidden ?? false) === (b.hidden ?? false) &&
    (a.formId ?? null) === (b.formId ?? null) &&
    (a.testId ?? null) === (b.testId ?? null) &&
    (a.fatorHId ?? null) === (b.fatorHId ?? null) &&
    (a.canResubmit ?? null) === (b.canResubmit ?? null) &&
    (a.contentText ?? null) === (b.contentText ?? null) &&
    (a.contentFileName ?? null) === (b.contentFileName ?? null) &&
    (a.contentFileUrl ?? null) === (b.contentFileUrl ?? null) &&
    (a.contentFileType ?? null) === (b.contentFileType ?? null) &&
    (a.externalEmbedMode ?? null) === (b.externalEmbedMode ?? null) &&
    (a.externalUrl ?? null) === (b.externalUrl ?? null) &&
    (a.externalPlatform ?? null) === (b.externalPlatform ?? null) &&
    (a.kolabInstanceId ?? null) === (b.kolabInstanceId ?? null)
  );
}

function phasesEqual(a: readonly JobPhase[], b: readonly JobPhase[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.id !== y.id) return false;
    if (x.name !== y.name) return false;
    if (x.type !== y.type) return false;
    if (x.startDate !== y.startDate) return false;
    if (x.endDate !== y.endDate) return false;
    if (x.endTime !== y.endTime) return false;
    if (x.stages.length !== y.stages.length) return false;
    for (let j = 0; j < x.stages.length; j++) {
      if (!stagesEqual(x.stages[j], y.stages[j])) return false;
    }
  }
  return true;
}

/** String não-vazia ou null — usado para ler campos do `module.config`. */
function configString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Lê o `module.config` de uma etapa de conteúdo nos campos de stage do SPA.
 * Shape (convenção docs/core-journey-stage-modals.md §2.c, estendida com a
 * mídia uploadada): `{ text, fileName, fileUrl, fileType }`.
 */
function readContentConfig(config: Record<string, unknown> | null | undefined): {
  contentText: string | null;
  contentFileName: string | null;
  contentFileUrl: string | null;
  contentFileType: 'pdf' | 'video' | null;
} {
  const rawType = configString(config?.fileType);
  return {
    contentText: configString(config?.text),
    contentFileName: configString(config?.fileName),
    contentFileUrl: configString(config?.fileUrl),
    contentFileType: rawType === 'pdf' || rawType === 'video' ? rawType : null,
  };
}

/**
 * Lê o `module.config` de uma etapa de teste externo (e Fator H) nos campos
 * de stage do SPA. Shape: `{ embedMode: boolean, externalUrl: string }`.
 */
function readExternalConfig(config: Record<string, unknown> | null | undefined): {
  externalEmbedMode: 'embed' | 'nova-aba' | 'redirect' | null;
  externalUrl: string | null;
  fatorHId: string | null;
  externalPlatform: 'url' | 'kolab' | null;
  kolabInstanceId: string | null;
} {
  const embedMode = config?.embedMode;
  const kolabInstanceId = configString(config?.kolabInstanceId);
  return {
    // `displayMode` (3 valores) tem precedência; etapas antigas derivam do
    // boolean `embedMode`.
    externalEmbedMode:
      configString(config?.displayMode) === 'redirect'
        ? 'redirect'
        : embedMode === true
          ? 'embed'
          : embedMode === false
            ? 'nova-aba'
            : null,
    externalUrl: configString(config?.externalUrl),
    // Fator H round-trips the selected exam id from `module.config` so editing
    // an existing stage re-seeds the picker (it has no top-level read field).
    fatorHId: configString(config?.fatorHId),
    // EU-539 — plataforma da etapa teste-externo. Kolab é reconhecida pelo
    // token `platform: 'kolab'` OU pela presença do instanceId (defensivo);
    // etapas antigas (só URL) leem como 'url'.
    externalPlatform:
      configString(config?.platform) === 'kolab' || kolabInstanceId ? 'kolab' : 'url',
    kolabInstanceId,
  };
}

function toComponentPhases(apiPhases: readonly ApiPhase[]): JobPhase[] {
  return apiPhases
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((p, idx) => ({
      id: p.id,
      index: idx + 1,
      name: p.name,
      // EU-165 phase-level fields. Core read boundary already applies defaults
      // for legacy rows; cast the closed enum from the SDK's generated string
      // union to our local `PhaseTypeBadge`.
      type: isPhaseTypeBadge(p.type) ? p.type : null,
      startDate: p.startDate,
      endDate: p.endDate,
      endTime: p.endTime,
      // FAS RN-06 — gate de delete inline.
      enrolledCount: p.enrolledCount,
      stages: p.stages
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((s) => ({
          id: s.id,
          name: s.name,
          type: s.type as StageBadgeType,
          startDate: s.startDate,
          endDate: s.endDate,
          endTime: s.endTime,
          hidden: s.hidden,
          // EU-764 RN-16 — travamento do "Mostrar", derivado pelo Core.
          hiddenLocked: (s as { hiddenLocked?: boolean }).hiddenLocked ?? false,
          formId: s.formId,
          // Teste/avaliação — o assessment vinculado vem como `assessmentId`
          // (resolvido via `assessment_module` no Core, surfaced pelo BFF read
          // mapper). Hidrata o picker `testId` do sheet para round-trip da
          // etapa de teste. `fatorHId`/contentText seguem no `module.config`.
          testId: (s as { assessmentId?: string | null }).assessmentId ?? null,
          // `fatorHId` is read from `module.config` via readExternalConfig (spread
          // below) — the read model has no top-level field for it.
          // The BFF's `canResubmit` is `boolean | null`; the SPA's sheet
          // draft uses `'sim' | 'nao' | ''`. Translate at the boundary so
          // the form re-seeds with the operator's prior pick on edit.
          canResubmit: s.canResubmit === true ? 'sim' : s.canResubmit === false ? 'nao' : null,
          // Etapa de conteúdo: texto + mídia vivem no `module.config` JSONB
          // (forwardado verbatim pelo BFF). Lidos via cast — o tipo gerado do
          // SDK só ganha `config` após o regen, mas o runtime já o envia.
          ...readContentConfig((s as { config?: Record<string, unknown> | null }).config),
          ...readExternalConfig((s as { config?: Record<string, unknown> | null }).config),
          // Config crua preservada para round-trip fiel — sem isso, tipos cujo
          // config o editor não autora (ex.: `complementacao-perfil` com
          // `{ requiredFields }`) seriam re-enviados com `config: null` e o Core
          // os trataria como "editados", disparando o gate de reabertura.
          rawConfig: (s as { config?: Record<string, unknown> | null }).config ?? null,
          // RN-06 — contagem de candidatos que já concluíram a etapa.
          // Drives o modal "Reabrir etapa" (RN-07) — quando > 0 o consultor
          // precisa decidir se reabre as respostas dos concluídos.
          completedCount: s.completedCount,
          // RN-10 — contagem total de respostas (concluídas ou não). Drives
          // o gate em `handleStageAction('delete')` — quando > 0 a etapa só
          // pode ser ocultada, nunca excluída.
          answerCount: s.answerCount,
        })),
    }));
}

/**
 * Inverse of `toComponentPhases`. Re-derives `position` from array order so
 * the operator's drag-or-delete interactions are the canonical source of
 * truth (1-based to match the visual `${index}ª Fase` rendering).
 *
 * `formId` / `testId` / `fatorHId` / `contentText` are not surfaced to the
 * SPA's stage state today (the catalogues are still mocked), so they round-
 * trip as null on save. Once the catalogues land they'll be looked up from
 * `sheetValues` when the stage is created.
 */
/**
 * Map the SPA's `JobPhase[]` shape into Core's `PUT /jobs/:id/steps` body.
 * After the trail→job absorption, recruitment-flow edits no longer go through
 * `PATCH /jobs/:id` with a `phases` JSONB column (BFF strips it now) — they
 * hit the relational `job_step` + `module` tables via replace-set.
 *
 *   - phase.type   → step.stepTypeKey
 *   - phase.name   → step.name (optional rename; null → lookup display name)
 *   - stage.type   → module.typeKey (`'form'` / `'profile_completion'`)
 *   - stage.formId → module.formId top-level (FK via `form_module` no Core)
 *
 * Phases with `type === null` are skipped (server requires stepTypeKey).
 * Stage-level `testId` / `fatorHId` / `contentText` / `contentFileName` are
 * not in Core's `module` schema; they're round-tripped client-only until the
 * Tier 1 normalization / module.config conventions land (ver docs/
 * core-journey-stage-modals.md). The BFF strips them when forwarding.
 */
/**
 * Phase ids gerados client-side seguem `phase-seed-N` / `phase-${ts}-${rnd}`,
 * NÃO são UUIDs. Só re-enviamos o `id` quando o Core nos devolveu um UUID
 * (= fase persistida), pra evitar 400 pela validação `z.string().uuid()` do
 * BFF. Phase nova → `id` ausente → Core gera UUID server-side.
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * EU-675 — Resposta do `PUT /jobs/:id/steps` (subconjunto que nos interessa: os
 * UUID que o Core atribuiu no replace-set + a `position` de cada step/module,
 * que usamos como CHAVE de casamento — ver `buildServerIdMap`).
 */
type SavedStepsResponse = {
  steps?: {
    id: string;
    position?: number;
    modules?: { id: string; position?: number }[];
  }[];
};

/** Casa um índice enviado (0-based) ao step/module da resposta pela `position`
 *  (1-based, como `toJobStepsBody` numera); cai para o índice do array só quando
 *  a resposta não traz `position`. */
function pickByPosition<T extends { position?: number }>(
  serverItems: readonly T[],
  sentIndex: number
): T | undefined {
  const hasPositions = serverItems.some((s) => typeof s.position === 'number');
  if (!hasPositions) return serverItems[sentIndex];
  return serverItems.find((s) => s.position === sentIndex + 1);
}

/**
 * EU-675 — Mapa `id client-side → UUID do servidor`. Casa a árvore ENVIADA
 * (fases com `type !== null`, numeradas por `toJobStepsBody` com `position` =
 * índice-na-lista + 1) com `saved.steps` PELA `position` — NÃO por índice de
 * array. A resposta do PUT é re-lida pelo Core (`getJobSteps`, `ORDER BY
 * position`) e não há garantia de contrato de que a ordem do array espelhe a de
 * envio; casar por índice adotaria o UUID na fase/etapa ERRADA se a ordem
 * divergir (ex.: inserção no meio) — corrupção silenciosa de fase persistida.
 * Sem a adoção do UUID, a fase/etapa nova mantém o id client-side e o smart-diff
 * a RE-INSERE em todo save — a duplicação descontrolada do bug.
 */
function buildServerIdMap(
  sent: readonly JobPhase[],
  saved: SavedStepsResponse
): Map<string, string> {
  const map = new Map<string, string>();
  const localSteps = sent.filter((p) => p.type !== null);
  const serverSteps = saved.steps ?? [];
  for (let i = 0; i < localSteps.length; i++) {
    const localPhase = localSteps[i];
    const serverStep = pickByPosition(serverSteps, i);
    if (!serverStep) continue;
    if (serverStep.id) map.set(localPhase.id, serverStep.id);
    const serverModules = serverStep.modules ?? [];
    for (let j = 0; j < localPhase.stages.length; j++) {
      const serverModule = pickByPosition(serverModules, j);
      if (serverModule?.id) map.set(localPhase.stages[j].id, serverModule.id);
    }
  }
  return map;
}

/**
 * EU-675 — Aplica os UUID do servidor sobre uma árvore local, trocando SÓ os ids
 * presentes no map. Fases/etapas adicionadas durante o request (ids ainda não
 * mapeados) são preservadas com o id client-side — serão persistidas no próximo
 * save.
 */
function applyServerIds(phases: readonly JobPhase[], idMap: Map<string, string>): JobPhase[] {
  if (idMap.size === 0) return phases.slice();
  return phases.map((phase) => {
    const nextPhaseId = idMap.get(phase.id);
    let stagesChanged = false;
    const stages = phase.stages.map((stage) => {
      const nextStageId = idMap.get(stage.id);
      if (!nextStageId) return stage;
      stagesChanged = true;
      return { ...stage, id: nextStageId };
    });
    if (!nextPhaseId && !stagesChanged) return phase;
    return { ...phase, ...(nextPhaseId ? { id: nextPhaseId } : {}), stages };
  });
}

function toJobStepsBody(
  phases: JobPhase[],
  modulesToReopen?: readonly string[]
): PutJobsIdStepsMutationRequest {
  const steps = phases
    .filter((p) => p.type !== null)
    .map((p, phaseIdx) => ({
      // FAS RN-06 — passar `id` de fases pré-existentes drive o gate de
      // delete no Core (`phase_has_enrollments` 422). Fases novas (id
      // client-side `phase-seed-…` / `phase-${ts}-…`) não viajam — Core
      // gera UUID server-side.
      ...(UUID_REGEX.test(p.id) ? { id: p.id } : {}),
      stepTypeKey: p.type as 'trilha-online',
      position: phaseIdx + 1,
      name: p.name && p.name.length > 0 ? p.name : null,
      modules: p.stages.map((s, stageIdx) => ({
        // RN-10 — passar `id` de etapas pré-existentes é o que faz o smart-diff
        // do Core RECONHECER a etapa como mantida (preserva `module_user_response`)
        // em vez de tratá-la como nova e dropar a antiga. Sem isso, TODO save
        // dropava todos os módulos e recriava — e, havendo qualquer resposta, o
        // gate `stage_has_answers` barrava o save inteiro (apontando pra etapa
        // respondida, não pra que o consultor tentou excluir). Etapas novas têm
        // id client-side `stage-…` (não-UUID) e não viajam — Core gera o UUID.
        ...(UUID_REGEX.test(s.id) ? { id: s.id } : {}),
        typeKey: s.type as 'form',
        name: s.name && s.name.length > 0 ? s.name : s.type,
        position: stageIdx + 1,
        required: null,
        // `module.config` JSONB (forwardado verbatim ao Core). Conteúdo grava
        // texto+mídia; teste externo (e Fator H) gravam modo + URL da
        // plataforma. Demais tipos não usam config.
        config:
          s.type === 'conteudo'
            ? {
                text: s.contentText ?? null,
                fileName: s.contentFileName ?? null,
                fileUrl: s.contentFileUrl ?? null,
                fileType: s.contentFileType ?? null,
              }
            : s.type === 'teste-externo' || s.type === 'fator-h'
              ? {
                  embedMode: s.externalEmbedMode === 'embed',
                  // Modo de exibição em 3 valores (o boolean `embedMode` fica
                  // por retrocompat): `redirect` = mesma aba com volta ao
                  // concluir (única opção integrada pra Kolab, que bloqueia
                  // iframe via X-Frame-Options).
                  displayMode:
                    s.externalEmbedMode === 'embed'
                      ? 'embed'
                      : s.externalEmbedMode === 'redirect'
                        ? 'redirect'
                        : 'new-tab',
                  externalUrl: s.externalUrl ?? null,
                  // Fator H carries the recruiter-selected exam id in `config`
                  // so the backend can record the module↔exam mapping on save
                  // (and the candidate iframe can mint a per-candidate URL).
                  // Other external tests use `externalUrl` directly.
                  ...(s.type === 'fator-h'
                    ? { fatorHId: s.fatorHId ?? null, platform: 'fator-h' }
                    : {}),
                  // EU-539 — etapa Kolab: `platform: 'kolab'` + o instanceId
                  // escolhido. A URL é gerada por candidato em runtime (o Core
                  // cria a execução na Kolab), então `externalUrl` fica null.
                  ...(s.type === 'teste-externo' && s.externalPlatform === 'kolab'
                    ? { platform: 'kolab', kolabInstanceId: s.kolabInstanceId ?? null }
                    : {}),
                }
              : // Tipos cujo config o editor não autora (form/teste/currículo/
                // complementação de perfil): re-envia a config crua do Core
                // verbatim. Mandar `null` aqui fazia o Core ver a config "mudar"
                // (ex.: `{ requiredFields }` → null) e tratar a etapa como
                // editada — disparando `module_reopen_decision_required` numa
                // etapa concluída que o consultor não tocou.
                (s.rawConfig ?? null),
        formId: s.formId ?? null,
        // Teste/avaliação — o assessment escolhido (Raio-X/Fit/Mapa) viaja como
        // `assessmentId`; o BFF mapeia `teste`→`tests` e o Core liga via
        // `assessment_module`. Null para os demais tipos.
        assessmentId: s.type === 'teste' ? (s.testId ?? null) : null,
        startDate: s.startDate ?? null,
        endDate: s.endDate ?? null,
        endTime: normalizeEndTime(s.endTime),
        canResubmit: s.canResubmit === 'sim' ? true : s.canResubmit === 'nao' ? false : null,
        hidden: s.hidden ?? false,
      })),
    }));
  // RN-08/09 — `modulesToReopen` só viaja quando há uma decisão tomada.
  // Array vazio = "Não pra todas" (decisão explícita); ausente = sem decisão.
  return modulesToReopen !== undefined
    ? { steps, modulesToReopen: [...modulesToReopen] }
    : { steps };
}

/**
 * Pad a single-digit hour ("8:48" → "08:48") so the value matches the BFF's
 * `^([01]\d|2[0-3]):[0-5]\d$` validator. Empty / unparseable input collapses
 * to null. Done here (frontend boundary) instead of relaxing the regex so
 * the stored format stays canonical for downstream consumers.
 */
function normalizeEndTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!match) return null;
  const hh = match[1].padStart(2, '0');
  const mm = match[2];
  const hour = Number(hh);
  if (hour < 0 || hour > 23) return null;
  return `${hh}:${mm}`;
}

export default function JobFlowPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/fluxo',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const putJobSteps = usePutJobSteps(jobId);
  const exportResponses = useExportStageResponses(jobId);

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: kanban em modo somente visualização.
  // O ref espelha o valor pro guard do `persist` (useCallback) sem inflar o
  // dependency array — mesmo pattern do `conflictRecoverRef`.
  const readOnly = isJobDetailReadOnly(job);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const jobName = job?.name ?? '';
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  // Derive companySlug from the loaded job so the Fator H catalogue
  // query can forward it to Core, which resolves the per-company api_key.
  // The query is disabled until the job loads (empty string → disabled).
  const companySlug = job?.companySlug ?? '';

  // Catalogue queries — Forms and Tests are company-agnostic.
  // Fator H is keyed by companySlug (per-company api_key resolution).
  const formsCatalogueQuery = useQuery(formsCatalogueQueryOptions);
  // EU-764 — o catálogo de testes passou a depender da VAGA (elegibilidade
  // por tipo de contratação), então a query é por `jobId`.
  const testsCatalogueQuery = useQuery(eligibleTestsQueryOptions(jobId));
  const fatorHCatalogueQuery = useQuery(fatorHCatalogueQueryOptions(companySlug));
  // EU-539 — instâncias Kolab (token global do ambiente; sem key por empresa).
  const kolabInstancesQuery = useQuery(kolabInstancesQueryOptions);

  const formOptions: SheetSelectOption[] = useMemo(
    () => (formsCatalogueQuery.data?.items ?? []).map((i) => ({ value: i.id, label: i.name })),
    [formsCatalogueQuery.data]
  );
  const testOptions: SheetSelectOption[] = useMemo(
    () =>
      (testsCatalogueQuery.data?.items ?? []).map((i) => ({
        value: i.id,
        label: i.name,
        // EU-764 — estes três campos eram DESCARTADOS aqui, e o `canResubmit`
        // que o Core já mandava era substituído por um match pelo nome do
        // teste. Preservá-los é o que faz a regra valer na tela.
        canResubmit: i.canResubmit,
        eligible: i.eligible,
        ineligibleReason: i.ineligibleReason,
      })),
    [testsCatalogueQuery.data]
  );
  const fatorHOptions: SheetSelectOption[] = useMemo(
    () => (fatorHCatalogueQuery.data?.items ?? []).map((i) => ({ value: i.id, label: i.name })),
    [fatorHCatalogueQuery.data]
  );
  const kolabOptions: SheetSelectOption[] = useMemo(
    () => (kolabInstancesQuery.data?.instances ?? []).map((i) => ({ value: i.id, label: i.name })),
    [kolabInstancesQuery.data]
  );
  // EU-539 — a UI do select distingue loading / erro de API / vazio.
  const kolabCatalogueState: 'loading' | 'error' | 'ready' = kolabInstancesQuery.isLoading
    ? 'loading'
    : kolabInstancesQuery.isError
      ? 'error'
      : 'ready';

  const [hasHydrated, setHasHydrated] = useState(false);
  // Auto-save fica gated em `seedingDone` para evitar disparar PATCH enquanto
  // a seed da pré-criação ainda está em voo (research §19). Sem isso, uma
  // edição do consultor durante o seed poderia perder a alteração.
  const [seedingDone, setSeedingDone] = useState(false);
  const [phases, setPhases] = useState<JobPhase[]>([]);

  // Spec 068 (AA-48) — badge FatorH no card do módulo. A mesma query do painel
  // (que também dispara o auto-reparo do vínculo no Core) roda quando o fluxo
  // tem módulo FatorH; o push no painel invalida a key e o badge acompanha.
  const hasFatorH = useMemo(
    () => phases.some((p) => p.stages.some((st) => st.type === 'fator-h')),
    [phases]
  );
  const fatorHSyncQuery = useQuery(fatorHSyncCandidatesQueryOptions(jobId, hasFatorH));
  const fatorHStatus = useMemo<FatorHCardStatus | null>(() => {
    if (!hasFatorH || !fatorHSyncQuery.data) return null;
    const { candidates, integration } = fatorHSyncQuery.data;
    if (
      integration &&
      (!integration.workspaceBound || !integration.hasKey || !integration.examMapped)
    ) {
      return { kind: 'integracao' };
    }
    const pending = candidates.filter((c) => !c.pushed).length;
    return pending > 0 ? { kind: 'pendentes', count: pending } : null;
  }, [hasFatorH, fatorHSyncQuery.data]);
  // Última versão das fases efetivamente persistidas no servidor. Usado para
  // (a) evitar PATCH redundante quando o estado volta ao último salvo,
  // (b) flush final no Voltar quando há diff vs último salvo.
  const lastSavedRef = useRef<readonly JobPhase[] | null>(null);
  // EU-675 — última árvore que ENTROU em persist (sucesso ou falha). Bloqueia a
  // re-tentativa automática do mesmo save falho (tempestade de PUTs); zera
  // implicitamente quando o consultor muda a árvore de novo.
  const lastAttemptedRef = useRef<readonly JobPhase[] | null>(null);
  // RN-08/09 — decisão "Reabrir etapa" pendente de envio. `null` = sem decisão
  // tomada (o PUT omite `modulesToReopen`); `[]` = "Não pra todas"; `[id, …]`
  // = "Sim" para as etapas listadas. Fica sticky até o próximo auto-save flush
  // consumi-la (debounce do `useDraftPersistence`), então é resetada no
  // `persist` em caso de sucesso.
  const pendingModulesToReopenRef = useRef<string[] | null>(null);

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activePhaseId, setActivePhaseId] = useState<string | null>(null);
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [sheetValues, setSheetValues] = useState<PhaseStageDraft>({
    ...emptySheetValues,
  });
  const [sheetErrors, setSheetErrors] = useState<Record<string, string>>({});

  const [isAddPhaseDialogOpen, setIsAddPhaseDialogOpen] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [addPhaseValues, setAddPhaseValues] = useState<PhaseDraft>({
    ...emptyPhaseDraft,
  });
  const [addPhaseErrors, setAddPhaseErrors] = useState<Record<string, string>>({});

  // EU-165 RN-07 — id da fase aguardando confirmação de exclusão. `null`
  // quando o modal de confirmação está fechado; o modal abre/fecha derivando
  // seu `open` deste estado.
  const [deletingPhaseId, setDeletingPhaseId] = useState<string | null>(null);

  // RN-11 — etapa aguardando confirmação de ocultar/excluir. `null` quando o
  // modal de confirmação está fechado.
  const [pendingStageAction, setPendingStageAction] = useState<{
    action: 'delete' | 'hide';
    phaseId: string;
    stageId: string;
  } | null>(null);

  // RN-06/RN-07 — edição de etapa aguardando a decisão de reabertura. `null`
  // quando o modal "Reabrir etapa" está fechado. A edição só é aplicada
  // depois que o consultor responde Sim/Não.
  const [pendingStageEdit, setPendingStageEdit] = useState<{
    phaseId: string;
    stageId: string;
    patch: StageEditPatch;
  } | null>(null);

  // RN-08/09 (recovery) — moduleIds que o Core 422'ou pedindo decisão de
  // reabertura (`module_reopen_decision_required`) num save que a detecção
  // inline do SPA não pegou (ex.: normalização ''↔null no roundtrip, ou stale).
  // Abre o MESMO StageReopenDialog com estes ids; a decisão arma
  // `pendingModulesToReopenRef` e RE-DISPARA o persist — em vez do antigo
  // dead-end (só toast → loop, edição nunca salvava).
  const [reopenRecovery, setReopenRecovery] = useState<string[] | null>(null);
  // Drawer "Sincronizar FatorH" (push manual/backfill dos inscritos da vaga),
  // aberto pelo ícone de refresh no card do módulo FatorH.
  const [fatorHSyncOpen, setFatorHSyncOpen] = useState(false);
  // Drawer "Detalhes" genérico. `moduleId` null = detalhes da etapa inteira
  // (aberto pelo header da fase); setado = detalhes de um módulo (ícone do card).
  const [detailsSheet, setDetailsSheet] = useState<{
    stepId: string;
    moduleId: string | null;
    title: string;
    /** Próxima fase (id + nome) — habilita "Passar de fase" na visão de etapa. */
    nextStepId: string | null;
    nextStepTitle: string | null;
  } | null>(null);

  // RN-13/RN-14 — trava do campo "Será possível reenviar a resposta?" no
  // editor de etapa, derivada do tipo + teste selecionado.
  const resubmitLock = useMemo(
    () => computeResubmitLock(sheetValues.type, sheetValues.testId ?? '', testOptions),
    [sheetValues.type, sheetValues.testId, testOptions]
  );

  /**
   * Erro toast utility — usado tanto pelo auto-save quanto pelo seed.
   * Branching idêntico à subseção Informações gerais (EU-158).
   */
  // MAP-086 — o último "Não foi possível salvar" na tela. Quando uma gravação
  // seguinte dá certo, ele deixa de ser verdade e sai, para o consultor não
  // ver erro e sucesso lado a lado depois de tentar de novo.
  const saveErrorToastRef = useRef<string | number | null>(null);

  const showSaveErrorToast = useCallback((err: unknown) => {
    const status = err instanceof JobMutationError ? err.status : 0;
    const details = err instanceof JobMutationError ? err.details : undefined;
    // FAS RN-06 — defesa server-side do phase-delete gate. Mesma mensagem
    // do gate inline pra UX consistente; também cobre race condition
    // (resposta criada entre o read do consultor e o save).
    if (status === 422 && details && details.code === 'phase_has_enrollments') {
      // Toast neutro com ação "Fechar" (Figma node 17596-7761) — sem ícone de
      // erro; mesma cópia do gate inline de RN-06.
      toast('Fase com usuários: não pode ser excluída', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      return;
    }
    // RN-10 stage delete gate — passa pelo mesmo PUT /jobs/:id/steps.
    if (status === 422 && details && details.code === 'stage_has_answers') {
      toast.error('Etapa com respostas: só pode ser ocultada');
      return;
    }
    // Etapa com respostas: tipo/catálogo travados. Defesa server-side — o sheet
    // já desabilita os selects; só cai aqui em race / SPA stale.
    if (status === 422 && details && details.code === 'module_type_locked_has_answers') {
      toast.error('Etapa com respostas: não é possível alterar o tipo ou o teste.');
      return;
    }
    // EU-764 RN-14 — o teste escolhido não se aplica a esta vaga. Defesa
    // server-side: a opção já chega desabilitada no dropdown, então isto só
    // aparece se a vaga virou de aprendiz enquanto o sheet estava aberto, ou
    // se alguém chamou a API direto.
    if (status === 422 && details && details.code === 'assessment_not_eligible') {
      toast.error('Este teste não está disponível para vagas de aprendiz.');
      return;
    }
    // RN-08/09 — defesa server-side do reopen: só aparece em race / SPA stale
    // (o fluxo normal sempre transmite a decisão Sim/Não). Ver
    // docs/core-stage-reopen-implementation.md §3.3.
    if (status === 422 && details && details.code === 'module_reopen_decision_required') {
      toast.error('Reabertura precisa de decisão. Reabra a edição da etapa e responda Sim ou Não.');
      return;
    }
    if (status === 404) {
      toast.error('Vaga ou programa não encontrado.');
    } else if (status === 401 || status === 403) {
      toast.error('Você não tem permissão para editar essa vaga.');
    } else {
      saveErrorToastRef.current = toast.error(
        'Não foi possível salvar as alterações. Tente novamente.'
      );
    }
  }, []);

  /**
   * Persist a phase list to the server. Updates `lastSavedRef` on success so
   * subsequent dedup checks know what's already in flight/saved.
   *
   * FAS RN-06 — em 422 `phase_has_enrollments` (race: candidato inscrito
   * entre o read e o save), revertemos pro último estado salvo pra que a
   * fase deletada otimisticamente reapareça no card. Mesma defesa para
   * `stage_has_answers` (RN-10 do stage doc).
   */
  // Self-heal on the server-side delete gates (RN-06 phase / RN-10 stage): when
  // Core rejects a delete with `phase_has_enrollments` / `stage_has_answers`,
  // the SPA's `enrolledCount`/`answerCount` was stale (an enrollment or a
  // candidate answer landed after the page loaded), so the inline gate let the
  // delete through. Refetch + re-hydrate so the affected phase/stage flips to
  // "hide-only" (trash disabled) instead of leaving the consultant able to
  // re-trigger the same rejected delete.
  const conflictRecoverRef = useRef<() => Promise<void>>(async () => undefined);
  conflictRecoverRef.current = async () => {
    try {
      const res = await jobQuery.refetch();
      if (res.data) {
        const rehydrated = toComponentPhases(res.data.phases);
        setPhases(rehydrated);
        lastSavedRef.current = rehydrated;
        return;
      }
    } catch {
      // fall through to the last-saved revert
    }
    if (lastSavedRef.current) setPhases(lastSavedRef.current.slice());
  };

  const persist = useCallback(
    async (next: readonly JobPhase[]): Promise<boolean> => {
      // RN-08/09 — decisão "Reabrir etapa" tomada desde o último save. `null`
      // = sem decisão pendente (omite o campo); `[]`/`[id]` = decisão a enviar.
      const reopenDecision = pendingModulesToReopenRef.current;
      // EU-569 — belt-and-suspenders: em modo visualização o auto-save nunca
      // dispara (as ações que o acionam estão desabilitadas na UI).
      if (readOnlyRef.current) return false;
      try {
        const saved = await putJobSteps.mutateAsync(
          toJobStepsBody(next.slice(), reopenDecision ?? undefined)
        );
        // EU-675 — adotar os UUID que o Core devolveu para os ids client-side
        // recém-criados. Sem isso a fase/etapa nova nunca casa o UUID e o
        // smart-diff a RE-INSERE em todo save (duplicação descontrolada).
        const idMap = buildServerIdMap(next, saved as SavedStepsResponse);
        // `prev` pode ter edições feitas durante o request — só trocamos os ids
        // mapeados, preservando o que foi adicionado nesse meio-tempo.
        if (idMap.size > 0) setPhases((prev) => applyServerIds(prev, idMap));
        lastSavedRef.current = applyServerIds(next, idMap);
        // Decisão consumida — reseta para que saves subsequentes não a reenviem.
        pendingModulesToReopenRef.current = null;
        // `!= null`: `dismiss(undefined)` dispensaria TODOS os toasts.
        if (saveErrorToastRef.current != null) {
          toast.dismiss(saveErrorToastRef.current);
          saveErrorToastRef.current = null;
        }
        return true;
      } catch (err) {
        const status = err instanceof JobMutationError ? err.status : 0;
        const code =
          err instanceof JobMutationError && err.details
            ? (err.details as { code?: string }).code
            : undefined;
        // RN-08/09 — reopen RECUPERÁVEL: em vez de toast dead-end (→ loop, com a
        // edição nunca salvando), abre o StageReopenDialog com os `moduleIds` do
        // 422; a decisão Sim/Não re-dispara o persist com `modulesToReopen`.
        if (status === 422 && code === 'module_reopen_decision_required') {
          const ids =
            (err instanceof JobMutationError && err.details
              ? (err.details as { moduleIds?: string[] }).moduleIds
              : undefined) ?? [];
          setReopenRecovery(ids);
          return false;
        }
        showSaveErrorToast(err);
        if (
          status === 422 &&
          (code === 'phase_has_enrollments' ||
            code === 'stage_has_answers' ||
            code === 'module_type_locked_has_answers')
        ) {
          // Re-sync from the server: reverte a troca otimista de tipo/catálogo
          // (que o Core recusou) e re-hidrata os counts pro gate inline.
          void conflictRecoverRef.current();
        }
        return false;
      }
    },
    [putJobSteps, showSaveErrorToast]
  );

  // Hidratação + pré-criação (RN-01). Roda uma única vez quando `job`
  // resolve. Se o servidor devolve `phases===[]`, dispara o seed do
  // `DEFAULT_PHASES` antes de habilitar o auto-save (gate `seedingDone`).
  // Refetches subsequentes não re-hidratam (flag `hasHydrated`).
  useEffect(() => {
    if (hasHydrated || !job) return;
    setHasHydrated(true);
    if (job.phases.length === 0) {
      // Render imediato dos 4 cards (UX otimista) + persistir no servidor.
      setPhases([...DEFAULT_PHASES]);
      void persist(DEFAULT_PHASES).finally(() => setSeedingDone(true));
      return;
    }
    const hydrated = toComponentPhases(job.phases);
    setPhases(hydrated);
    lastSavedRef.current = hydrated;
    setSeedingDone(true);
  }, [job, hasHydrated, persist]);

  // Bounce back to the list on a 404 (out-of-scope or missing job).
  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  function handleAddPhase() {
    setAddPhaseValues({ ...emptyPhaseDraft });
    setAddPhaseErrors({});
    setEditingPhaseId(null);
    setIsAddPhaseDialogOpen(true);
  }

  function handleAddPhaseDialogOpenChange(open: boolean) {
    setIsAddPhaseDialogOpen(open);
    if (!open) {
      setAddPhaseErrors({});
      setEditingPhaseId(null);
    }
  }

  function handleAddPhaseFieldChange<K extends keyof PhaseDraft>(field: K, value: PhaseDraft[K]) {
    setAddPhaseValues((prev) => ({ ...prev, [field]: value }));
  }

  function handleAddPhaseSubmit() {
    const errs: Record<string, string> = {};
    if (!addPhaseValues.name.trim()) errs.name = 'Informe o nome da fase';
    // EU-165 RN-06 — Tipo é obrigatório TANTO em add quanto em edit; antes
    // estava `!editingPhaseId && !type`, mas RN-06 manda todos os campos
    // aparecerem no edit e o Tipo continua sendo o discriminador interno
    // para análise de funil (RN-07).
    if (!addPhaseValues.type) errs.type = 'Selecione o tipo';
    if (Object.keys(errs).length > 0) {
      setAddPhaseErrors(errs);
      return;
    }

    const trimmedName = addPhaseValues.name.trim();
    const typeValue = isPhaseTypeBadge(addPhaseValues.type) ? addPhaseValues.type : null;
    const startDate = addPhaseValues.startDate || null;
    const endDate = addPhaseValues.endDate || null;
    // Vazio cai para '23:59' (RN-05) — preserva pre-fill mesmo se o
    // consultor limpou o campo na correria.
    const endTime = addPhaseValues.endTime || '23:59';

    if (editingPhaseId) {
      setPhases((prev) =>
        prev.map((phase) =>
          phase.id === editingPhaseId
            ? {
                ...phase,
                name: trimmedName,
                type: typeValue,
                startDate,
                endDate,
                endTime,
              }
            : phase
        )
      );
      handleAddPhaseDialogOpenChange(false);
      toast('Fase atualizada', { action: { label: 'Fechar', onClick: () => undefined } });
      return;
    }

    const nextIndex = phases.length + 1;
    setPhases((prev) => [
      ...prev,
      {
        id: `phase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        index: nextIndex,
        name: trimmedName,
        type: typeValue,
        startDate,
        endDate,
        endTime,
        stages: [],
      },
    ]);
    handleAddPhaseDialogOpenChange(false);
    // EU-165 Juliana TODO #5 — toast de feedback após adicionar fase.
    toast('Fase adicionada', { action: { label: 'Fechar', onClick: () => undefined } });
  }

  function handlePhaseAction(action: PhaseAction, phaseId: string) {
    if (action === 'details') {
      // Detalhes da ETAPA inteira: quem está na fase e o estado de cada um
      // (Concluído = terminou todos os módulos). `moduleId` null = visão de fase.
      // A próxima fase (por ordem) habilita "Passar de fase".
      const idx = phases.findIndex((p) => p.id === phaseId);
      const phase = phases[idx];
      if (!phase) return;
      const next = phases[idx + 1] ?? null;
      setDetailsSheet({
        stepId: phaseId,
        moduleId: null,
        title: phase.name,
        nextStepId: next?.id ?? null,
        nextStepTitle: next?.name ?? null,
      });
      return;
    }
    if (action === 'edit') {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;
      // RN-06 — todos os 5 campos chegam pré-preenchidos com o estado atual
      // da fase. Antes só `name` era seedado e `type` era escondido; agora
      // o consultor vê e pode editar Tipo, Começa, Encerra e Horário também.
      setAddPhaseValues({
        name: phase.name,
        type: phase.type ?? '',
        startDate: phase.startDate ?? '',
        endDate: phase.endDate ?? '',
        endTime: phase.endTime ?? '23:59',
      });
      setAddPhaseErrors({});
      setEditingPhaseId(phaseId);
      setIsAddPhaseDialogOpen(true);
      return;
    }
    if (action === 'delete') {
      // FAS RN-06 — gate inline de inscritos. O Core agora expõe
      // `enrolledCount` por fase no read (`GET /jobs/:id`). Se > 0, NÃO
      // abre o modal de confirmação — toast direto. Defesa server-side
      // continua via 422 `phase_has_enrollments` (ver `showSaveErrorToast`).
      const phase = phases.find((p) => p.id === phaseId);
      if (phase && (phase.enrolledCount ?? 0) > 0) {
        // Toast neutro com ação "Fechar" (Figma node 17596-7761) — sem ícone
        // de erro; o botão verde primário dispensa o toast.
        toast('Fase com usuários: não pode ser excluída', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
        return;
      }
      // EU-165 RN-07 — não exclui na hora: abre o modal de confirmação
      // dedicado. A remoção efetiva acontece em `handleConfirmDeletePhase`,
      // só após o consultor confirmar explicitamente.
      setDeletingPhaseId(phaseId);
    }
  }

  /**
   * EU-165 RN-07 — exclusão efetiva da fase, disparada apenas após
   * confirmação explícita no `JobConfirmDialog`. Remove a fase e
   * re-indexa as restantes (1-based). O auto-save (`useDraftPersistence`)
   * persiste a nova lista na próxima janela de debounce.
   */
  async function handleConfirmDeletePhase() {
    const phaseId = deletingPhaseId;
    if (!phaseId) return;
    const next = phases
      .filter((phase) => phase.id !== phaseId)
      .map((phase, index) => ({
        ...phase,
        index: index + 1,
        // Preserve the operator-typed name. The legacy "rename to ${n}ª
        // Fase" behavior wiped that on every delete; keeping it stable
        // means the saved row also keeps the operator's chosen labels.
      }));
    setPhases(next);
    setDeletingPhaseId(null);
    // Persiste síncrono p/ o toast só confirmar após o servidor aceitar. Em
    // 422 `phase_has_enrollments` (race: candidato chegou na fase entre o read
    // e o delete) `persist` reverte e mostra o erro; suprimimos o sucesso falso.
    const ok = await persist(next);
    if (ok) {
      toast('Fase excluída', { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }

  /** Fecha o modal de confirmação de exclusão sem remover a fase (RN-08). */
  function handleDeletePhaseDialogOpenChange(open: boolean) {
    if (!open) setDeletingPhaseId(null);
  }

  function handleAddStage(phaseId: string) {
    setActivePhaseId(phaseId);
    setEditingStageId(null);
    setSheetValues({ ...emptySheetValues });
    setSheetErrors({});
    setIsSheetOpen(true);
  }

  /**
   * RN-11 — alterna `hidden` de uma etapa. Usado tanto pelo reexibir imediato
   * quanto pela confirmação de ocultar. O toast confirma o novo estado.
   */
  function applyToggleHidden(phaseId: string, stageId: string) {
    let willBeHidden = false;
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId
          ? {
              ...phase,
              stages: phase.stages.map((stage) => {
                if (stage.id !== stageId) return stage;
                willBeHidden = !stage.hidden;
                return { ...stage, hidden: willBeHidden };
              }),
            }
          : phase
      )
    );
    // Auto-save (EU-165 RN-09) consolida e persiste — a confirmação visual já
    // vem do ícone (olho/olho cortado); o toast indica o novo estado.
    toast(willBeHidden ? 'Etapa ocultada' : 'Etapa visível novamente', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  /**
   * RN-11 — remove uma etapa da fase. Disparado só após confirmação.
   *
   * Persiste de forma síncrona (não pelo debounce do auto-save) para que o
   * toast de sucesso só apareça DEPOIS do servidor confirmar. Em 422
   * `stage_has_answers` (etapa com respostas criadas entre o read e o delete —
   * race que o gate inline não pega), `persist` já reverte o estado e mostra
   * "Etapa com respostas: só pode ser ocultada"; aqui só suprimimos o toast de
   * sucesso falso. O dedup por `phasesEqual` neutraliza o save do debounce.
   */
  async function applyDeleteStage(phaseId: string, stageId: string) {
    const next = phases.map((phase) =>
      phase.id === phaseId
        ? { ...phase, stages: phase.stages.filter((stage) => stage.id !== stageId) }
        : phase
    );
    setPhases(next);
    const ok = await persist(next);
    if (ok) {
      toast('Etapa excluída', { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }

  /** Aplica um patch de edição a uma etapa existente (RN-05 / RN-06). */
  function applyStageEdit(phaseId: string, stageId: string, patch: StageEditPatch) {
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId
          ? {
              ...phase,
              stages: phase.stages.map((stage) =>
                stage.id === stageId ? { ...stage, ...patch } : stage
              ),
            }
          : phase
      )
    );
  }

  // Puts the PhaseSheet into edit mode for a concrete stage object. Shared by
  // the kebab "Editar" action (looks the stage up in `phases`) and by the
  // create flow, which calls it right after adding the stage so the already-
  // open sheet transitions in place from "add" to "edit" — the stage is passed
  // directly so it doesn't depend on the not-yet-flushed `phases` state.
  function openStageForEdit(phaseId: string, stage: JobStage) {
    setActivePhaseId(phaseId);
    setEditingStageId(stage.id);
    setSheetValues({
      name: stage.name,
      type: stage.type,
      formId: stage.formId ?? '',
      testId: stage.testId ?? '',
      fatorHId: stage.fatorHId ?? '',
      contentText: stage.contentText ?? '',
      contentFileName: stage.contentFileName ?? null,
      contentFileUrl: stage.contentFileUrl ?? null,
      contentFileType: stage.contentFileType ?? null,
      contentUploading: false,
      externalEmbedMode: stage.externalEmbedMode ?? '',
      externalUrl: stage.externalUrl ?? '',
      externalPlatform: stage.externalPlatform ?? '',
      kolabInstanceId: stage.kolabInstanceId ?? '',
      startDate: stage.startDate ?? '',
      endDate: stage.endDate ?? '',
      endTime: stage.endTime ?? '',
      canResubmit: stage.canResubmit ?? '',
    });
    setSheetErrors({});
    setIsSheetOpen(true);
  }

  function handleStageAction(action: StageAction, phaseId: string, stageId: string) {
    if (action === 'details') {
      const stage = phases.find((p) => p.id === phaseId)?.stages.find((s) => s.id === stageId);
      if (!stage) return;
      // FatorH mantém o painel dedicado (Enviar/Resultados + push job-level); o
      // ícone de info abre ELE. Os demais módulos abrem o drawer read-only de
      // detalhes (quem está no módulo + Pendente/Em andamento/Concluído).
      if (stage.type === 'fator-h') {
        setFatorHSyncOpen(true);
        return;
      }
      setDetailsSheet({
        stepId: phaseId,
        moduleId: stageId,
        title: stage.name,
        // Visão de módulo é read-only — sem "Passar de fase".
        nextStepId: null,
        nextStepTitle: null,
      });
      return;
    }
    if (action === 'export') {
      // Exporta a base de respostas da etapa (module) em XLSX. O `stageId` é o
      // id do module no Core; o hook busca via BFF e dispara o download.
      const stage = phases.find((p) => p.id === phaseId)?.stages.find((s) => s.id === stageId);
      if (!stage) return;
      exportResponses.mutate({ moduleId: stageId, stageName: stage.name });
      return;
    }
    if (action === 'toggle-hidden') {
      const stage = phases.find((p) => p.id === phaseId)?.stages.find((s) => s.id === stageId);
      if (!stage) return;
      // RN-11 — reexibir uma etapa oculta é imediato (ação não destrutiva);
      // ocultar exige confirmação explícita via modal.
      if (stage.hidden) {
        applyToggleHidden(phaseId, stageId);
        return;
      }
      setPendingStageAction({ action: 'hide', phaseId, stageId });
      return;
    }
    if (action === 'delete') {
      // RN-10 — gate inline: etapa com respostas não pode ser excluída, só
      // ocultada. Toast informa o consultor e (quando a etapa estiver
      // visível) já abre o fluxo de hide; quando já está oculta, no-op.
      // Defesa server-side adicional: 422 `stage_has_answers` no PUT/jobs/:id/steps.
      const stage = phases.find((p) => p.id === phaseId)?.stages.find((s) => s.id === stageId);
      if (stage && (stage.answerCount ?? 0) > 0) {
        toast.error('Etapa com respostas: só pode ser ocultada');
        if (!stage.hidden) {
          setPendingStageAction({ action: 'hide', phaseId, stageId });
        }
        return;
      }
      // RN-11 — não exclui na hora: abre o modal de confirmação dedicado. A
      // remoção efetiva acontece em `handleConfirmStageAction`.
      setPendingStageAction({ action: 'delete', phaseId, stageId });
      return;
    }
    if (action === 'edit') {
      const phase = phases.find((p) => p.id === phaseId);
      const stage = phase?.stages.find((s) => s.id === stageId);
      if (!stage) return;
      // EU-165 Juliana TODO #4 — abrir o edit já hidratado (texto, arquivo,
      // datas, catálogo) em vez de campos vazios. Mapeamento em openStageForEdit.
      openStageForEdit(phaseId, stage);
    }
  }

  /** RN-11 — aplica a ação de etapa (ocultar/excluir) confirmada no modal. */
  function handleConfirmStageAction() {
    const pending = pendingStageAction;
    if (!pending) return;
    if (pending.action === 'delete') {
      void applyDeleteStage(pending.phaseId, pending.stageId);
    } else {
      applyToggleHidden(pending.phaseId, pending.stageId);
    }
    setPendingStageAction(null);
  }

  /** Fecha o modal de confirmação de etapa sem aplicar a ação (RN-11). */
  function handleStageConfirmOpenChange(open: boolean) {
    if (!open) setPendingStageAction(null);
  }

  /**
   * RN-07/RN-08/RN-09 — resposta do consultor ao modal "Reabrir etapa".
   * Aplica a edição pendente e arma a decisão de reabertura no
   * `pendingModulesToReopenRef`; o próximo auto-save (`persist`) a transmite
   * ao Core no campo `modulesToReopen` do `PUT /jobs/:id/steps`:
   *   - "Sim"  → reabre apenas esta etapa (`[stageId]`) para quem já concluiu.
   *   - "Não"  → array vazio (`[]`) = decisão explícita "não reabrir".
   * `stageId` é o UUID do `module` no Core (o modal só dispara quando
   * `completedCount > 0`, logo a etapa é pré-existente e persistida).
   * Ver docs/core-stage-reopen-implementation.md.
   */
  function handleStageReopenDecision(reopen: boolean) {
    // Recovery (422 do Core): o diálogo abriu SEM edição inline pendente — não há
    // patch a aplicar. Arma a decisão pros `moduleIds` do 422 e RE-DISPARA o
    // persist (as fases não mudaram, então o auto-save por diff não roda sozinho).
    if (reopenRecovery) {
      pendingModulesToReopenRef.current = reopen ? reopenRecovery.slice() : [];
      setReopenRecovery(null);
      void persist(phases);
      return;
    }
    const pending = pendingStageEdit;
    if (!pending) return;
    applyStageEdit(pending.phaseId, pending.stageId, pending.patch);
    pendingModulesToReopenRef.current = reopen ? [pending.stageId] : [];
    setPendingStageEdit(null);
    // SEL — AC "Reabrir etapa" (Sim/Não): toast literal "Etapa editada".
    toast('Etapa editada', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  /** Fecha o modal "Reabrir etapa" sem aplicar a edição/decisão pendente (RN-07). */
  function handleReopenDialogOpenChange(open: boolean) {
    if (!open) {
      setPendingStageEdit(null);
      setReopenRecovery(null);
    }
  }

  function handleSheetOpenChange(open: boolean) {
    setIsSheetOpen(open);
    if (!open) {
      setActivePhaseId(null);
      setEditingStageId(null);
      setSheetErrors({});
    }
  }

  function handleSheetFieldChange<K extends keyof PhaseStageDraft>(
    field: K,
    value: PhaseStageDraft[K]
  ) {
    setSheetValues((prev) => ({ ...prev, [field]: value }));
  }

  function handleSheetContentFileChange(file: File | null) {
    if (!file) {
      // Remoção do arquivo — limpa nome + URL + tipo.
      setSheetValues((prev) => ({
        ...prev,
        contentFileName: null,
        contentFileUrl: null,
        contentFileType: null,
        contentUploading: false,
      }));
      return;
    }
    // Display otimista do nome + trava o submit enquanto sobe para o S3.
    setSheetValues((prev) => ({
      ...prev,
      contentFileName: file.name,
      contentUploading: true,
    }));
    void uploadJobContentAsset(jobId, file)
      .then((result) => {
        setSheetValues((prev) => ({
          ...prev,
          contentFileName: result.fileName,
          contentFileUrl: result.url,
          contentFileType: result.fileType,
          contentUploading: false,
        }));
      })
      .catch(() => {
        // Falha de upload — reverte o arquivo e avisa. O consultor pode
        // tentar de novo; o save fica liberado (sem mídia pendente).
        setSheetValues((prev) => ({
          ...prev,
          contentFileName: null,
          contentFileUrl: null,
          contentFileType: null,
          contentUploading: false,
        }));
        toast.error('Não foi possível enviar o arquivo. Tente novamente.');
      });
  }

  function validateSheet(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!sheetValues.name.trim()) errs.name = 'Informe o nome da etapa';
    if (!sheetValues.type) errs.type = 'Selecione o tipo';
    // Assim que um Tipo é escolhido, os campos dependentes ficam visíveis
    // (catálogo, canResubmit, datas) — em add e edit. Validar todos aqui
    // garante que a etapa seja criada já completa num único passo, em vez de
    // persistir uma etapa intermediária meio-configurada (bug reportado).
    if (sheetValues.type) {
      if (sheetValues.type === 'form' && !sheetValues.formId) {
        errs.formId = 'Selecione o formulário';
      }
      if (sheetValues.type === 'teste' && !sheetValues.testId) {
        errs.testId = 'Selecione o teste';
      }
      if (sheetValues.type === 'fator-h' && !sheetValues.fatorHId) {
        errs.fatorHId = 'Selecione o Fator H';
      }
      // EU-539 — etapa Kolab exige a instância escolhida no catálogo.
      if (
        sheetValues.type === 'teste-externo' &&
        sheetValues.externalPlatform === 'kolab' &&
        !sheetValues.kolabInstanceId
      ) {
        errs.kolabInstanceId = 'Selecione o teste Kolab';
      }
      // RN-13/RN-14 — quando o campo está travado, a regra de negócio impõe o
      // valor; não exigir escolha manual do consultor.
      if (!resubmitLock.locked && !sheetValues.canResubmit) {
        errs.canResubmit = 'Selecione uma opção';
      }
      if (!sheetValues.startDate) errs.startDate = 'Selecione a data de início';
      if (!sheetValues.endDate) errs.endDate = 'Selecione a data de encerramento';
    }
    return errs;
  }

  // MAP-086 — criação de etapa em voo. A ref fecha a porta no MESMO tick do
  // clique (duplo clique); o state liga o loading do botão.
  const addingStageRef = useRef(false);
  const [addingStage, setAddingStage] = useState(false);

  function handleSheetSubmit() {
    const errs = validateSheet();
    if (Object.keys(errs).length > 0) {
      setSheetErrors(errs);
      return;
    }
    if (!activePhaseId) return;

    // Compute the catalogue ids the new/updated stage should round-trip.
    // Each stage type uses one slot — clear the others so a type-switch
    // doesn't carry stale values from the prior selection (e.g. picking
    // "Formulário" then switching to "Teste" before submit).
    const type = stageBadgeFromType(sheetValues.type);
    const formId = type === 'form' && sheetValues.formId ? sheetValues.formId : null;
    const testId = type === 'teste' && sheetValues.testId ? sheetValues.testId : null;
    const fatorHId = type === 'fator-h' && sheetValues.fatorHId ? sheetValues.fatorHId : null;
    // RN-13/RN-14 — quando o campo está travado, a regra de negócio impõe o
    // valor; caso contrário usa a escolha do consultor.
    const canResubmit: 'sim' | 'nao' | null = resubmitLock.locked
      ? resubmitLock.value
      : sheetValues.canResubmit === 'sim'
        ? 'sim'
        : sheetValues.canResubmit === 'nao'
          ? 'nao'
          : null;

    // EU-165 Juliana TODO #4 — etapas do tipo `conteudo` round-trip
    // `contentText` + `contentFileName`. Outros tipos limpam pra evitar
    // contaminação cross-type quando o consultor troca o tipo no edit.
    const contentText = type === 'conteudo' ? (sheetValues.contentText ?? null) : null;
    const contentFileName = type === 'conteudo' ? (sheetValues.contentFileName ?? null) : null;
    const contentFileUrl = type === 'conteudo' ? (sheetValues.contentFileUrl ?? null) : null;
    const contentFileType = type === 'conteudo' ? (sheetValues.contentFileType ?? null) : null;

    // Teste externo + Fator H — URL da plataforma. Limpa para os demais tipos
    // ao trocar.
    const isExternal = type === 'teste-externo' || type === 'fator-h';
    // EU-539 — plataforma da etapa teste-externo. Kolab zera a URL (gerada por
    // candidato em runtime); a plataforma genérica zera o instanceId.
    const externalPlatform: 'url' | 'kolab' | null =
      type === 'teste-externo'
        ? sheetValues.externalPlatform === 'kolab'
          ? 'kolab'
          : 'url'
        : null;
    // "Como o teste será exibido" — teste-externo é AUTOMÁTICO (produto
    // 2026-07-17): via URL → embedado (iframe); Kolab → mesma aba com retorno
    // ao concluir (a Kolab bloqueia iframe via X-Frame-Options). Fator H
    // mantém a escolha manual do consultor (comportamento pré-EU-539).
    const externalEmbedMode: 'embed' | 'nova-aba' | 'redirect' | null = !isExternal
      ? null
      : type === 'fator-h'
        ? sheetValues.externalEmbedMode === 'embed'
          ? 'embed'
          : sheetValues.externalEmbedMode === 'nova-aba'
            ? 'nova-aba'
            : null
        : externalPlatform === 'kolab'
          ? 'redirect'
          : 'embed';
    const kolabInstanceId =
      externalPlatform === 'kolab' ? (sheetValues.kolabInstanceId ?? null) : null;
    const externalUrl =
      isExternal && externalPlatform !== 'kolab' ? sheetValues.externalUrl?.trim() || null : null;

    // Preserva a `module.config` crua das etapas cujo config o editor não
    // autora (form/teste/currículo/complementação de perfil). Conteúdo e teste
    // externo/Fator H reconstroem a config no write a partir dos campos, então
    // não dependem disso. Ao TROCAR o tipo, zera — senão o write reenviaria a
    // config do tipo antigo. Mantém quando o tipo não muda, preservando ex.
    // `{ requiredFields }` de uma complementação de perfil que o consultor só
    // renomeou.
    const prevStage = phases
      .find((p) => p.id === activePhaseId)
      ?.stages.find((s) => s.id === editingStageId);
    const rawConfig: Record<string, unknown> | null =
      prevStage && prevStage.type === type ? (prevStage.rawConfig ?? null) : null;

    if (editingStageId) {
      const stagePatch: StageEditPatch = {
        name: sheetValues.name,
        type,
        startDate: sheetValues.startDate || null,
        endDate: sheetValues.endDate || null,
        endTime: sheetValues.endTime ? sheetValues.endTime : null,
        formId,
        testId,
        fatorHId,
        canResubmit,
        contentText,
        contentFileName,
        contentFileUrl,
        contentFileType,
        externalEmbedMode,
        externalUrl,
        externalPlatform,
        kolabInstanceId,
        rawConfig,
      };
      // RN-06 — se a etapa já tem candidatos que a concluíram, a edição NÃO é
      // aplicada na hora: abre o modal "Reabrir etapa" (RN-07) e a aplicação
      // efetiva acontece em `handleStageReopenDecision` após a resposta.
      const editedStage = phases
        .find((p) => p.id === activePhaseId)
        ?.stages.find((s) => s.id === editingStageId);
      if (editedStage && (editedStage.completedCount ?? 0) > 0) {
        setPendingStageEdit({ phaseId: activePhaseId, stageId: editingStageId, patch: stagePatch });
        handleSheetOpenChange(false);
        return;
      }
      applyStageEdit(activePhaseId, editingStageId, stagePatch);
      handleSheetOpenChange(false);
      // EU-165 Juliana TODO #5 — toast confirma edição da etapa (copy AC).
      toast('Etapa editada', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      return;
    }

    const newStage: JobStage = {
      id: `stage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: sheetValues.name,
      type,
      startDate: sheetValues.startDate || null,
      endDate: sheetValues.endDate || null,
      endTime: sheetValues.endTime ? sheetValues.endTime : null,
      hidden: false,
      formId,
      testId,
      fatorHId,
      canResubmit,
      contentText,
      contentFileName,
      contentFileUrl,
      contentFileType,
      externalEmbedMode,
      externalUrl,
      externalPlatform,
      kolabInstanceId,
      // Etapa nova: sem config crua preservada (null). Conteúdo/teste externo
      // reconstroem a config no write a partir dos campos acima.
      rawConfig,
    };
    const next = phases.map((phase) =>
      phase.id === activePhaseId ? { ...phase, stages: [...phase.stages, newStage] } : phase
    );
    // MAP-086 (QA EU-764, 24/09) — a etapa nova é GRAVADA antes de aparecer.
    // Antes o card entrava na fase na hora, o toast de sucesso saía junto, e o
    // auto-save gravava depois: com a rede caindo no clique, a tela mostrava
    // "Não foi possível salvar" E "Etapa adicionada com sucesso", e o card
    // fingia existir até a próxima alteração (sumia se o consultor saísse).
    // Agora: sucesso → card + toast; falha → só o erro (do `persist`), o sheet
    // continua aberto com tudo preenchido e "Adicionar etapa" tenta de novo.
    if (addingStageRef.current) return;
    addingStageRef.current = true;
    setAddingStage(true);
    void persist(next).then((ok) => {
      addingStageRef.current = false;
      setAddingStage(false);
      if (!ok) return;
      // `persist` guardou em `lastSavedRef` a árvore com os UUIDs do Core; é
      // ela que entra na tela, para o auto-save seguinte reconhecer a etapa.
      setPhases((lastSavedRef.current ?? next).slice());
      handleSheetOpenChange(false);
      // EU-165 Juliana TODO #5 — toast confirma adição da etapa.
      toast('Etapa adicionada com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    });
  }

  // Auto-save com debounce via design-system hook. `save` precisa ser
  // referencialmente estável — `useDraftPersistence` reseta o timer sempre que
  // sua identidade muda — então acessamos contexto recente por ref. Gate
  // `seedingDone` evita disparar PATCH durante a seed da pré-criação.
  const saveCtxRef = useRef({ seedingDone, persist, isSaving: false });
  saveCtxRef.current = { seedingDone, persist, isSaving: putJobSteps.isPending };

  const save = useCallback((next: readonly JobPhase[]) => {
    const ctx = saveCtxRef.current;
    if (!ctx.seedingDone) return;
    // EU-675 — nunca dispara PUT concorrente: um save em andamento bloqueia o
    // próximo. A mudança pendente é persistida quando o PUT atual termina (o
    // `useDraftPersistence` re-arma o debounce no re-render pós-mutation).
    if (ctx.isSaving) return;
    if (lastSavedRef.current && phasesEqual(next, lastSavedRef.current)) return;
    // EU-675 — não re-tenta automaticamente o MESMO save que acabou de falhar
    // (evita a tempestade de retentativas). Uma mudança real do consultor
    // muda a árvore → `lastAttemptedRef` deixa de casar → tenta de novo.
    if (lastAttemptedRef.current && phasesEqual(next, lastAttemptedRef.current)) return;
    lastAttemptedRef.current = next;
    void ctx.persist(next);
  }, []);

  useDraftPersistence(
    () => ({ phases }),
    () => undefined,
    {
      load: () => null,
      save: (snapshot) => save((snapshot as { phases: JobPhase[] }).phases),
      enabled: hasHydrated && seedingDone,
      debounceMs: AUTOSAVE_DEBOUNCE_MS,
    }
  );

  /**
   * Reorder das fases via drag-and-drop horizontal (US-18). `arrayMove` do
   * `@dnd-kit/sortable` reordena pelo index; recomputamos `index` 1-based
   * para refletir na UI ("1ª Fase", "2ª Fase", ...). Auto-save consolidado
   * dispara via `useDraftPersistence` na próxima janela de debounce; dedup
   * por `phasesEqual` bloqueia PATCH se o drop foi no mesmo lugar.
   */
  function handleReorder(fromId: string, toId: string) {
    setPhases((prev) => {
      const fromIdx = prev.findIndex((p) => p.id === fromId);
      const toIdx = prev.findIndex((p) => p.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      if (fromIdx === toIdx) return prev;
      return arrayMove(prev, fromIdx, toIdx).map((p, i) => ({ ...p, index: i + 1 }));
    });
  }

  /**
   * SEL-013/SEL-014 — commit do drop de uma etapa. Cobre 3 cenários:
   * (a) reorder dentro da mesma fase, (b) move pra outra fase, (c) no-op se
   * a etapa não existe (race). Etapas com `answerCount > 0` já foram
   * bloqueadas no `SortableStageCard` (`useSortable({disabled})`) — então
   * essa callback nunca dispara pra elas. O auto-save consolidado persiste
   * via `PUT /jobs/:id/steps` na próxima janela de debounce.
   */
  function handleStageDrop(
    stageId: string,
    fromPhaseId: string,
    toPhaseId: string,
    toIndex: number
  ) {
    setPhases((prev) => {
      const fromPhase = prev.find((p) => p.id === fromPhaseId);
      const stage = fromPhase?.stages.find((s) => s.id === stageId);
      if (!fromPhase || !stage) return prev;

      // Mesma fase: reorder via splice. `toIndex` é a posição final desejada
      // depois da remoção, então clamp se necessário.
      if (fromPhaseId === toPhaseId) {
        const without = fromPhase.stages.filter((s) => s.id !== stageId);
        const safeIdx = Math.min(Math.max(toIndex, 0), without.length);
        const reordered = [...without.slice(0, safeIdx), stage, ...without.slice(safeIdx)];
        // No-op se a ordem efetiva não mudou (drop no mesmo lugar).
        if (reordered.every((s, i) => s.id === fromPhase.stages[i].id)) return prev;
        return prev.map((p) => (p.id === fromPhaseId ? { ...p, stages: reordered } : p));
      }

      // Cross-phase: remove da origem, insere no destino.
      return prev.map((p) => {
        if (p.id === fromPhaseId) {
          return { ...p, stages: p.stages.filter((s) => s.id !== stageId) };
        }
        if (p.id === toPhaseId) {
          const safeIdx = Math.min(Math.max(toIndex, 0), p.stages.length);
          return {
            ...p,
            stages: [...p.stages.slice(0, safeIdx), stage, ...p.stages.slice(safeIdx)],
          };
        }
        return p;
      });
    });
  }

  async function handleBack() {
    // Flush final: se há diff vs último salvo, persistir antes de navegar.
    if (lastSavedRef.current && !phasesEqual(phases, lastSavedRef.current)) {
      await persist(phases);
    }
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  // Loading state — primeiro carregamento da vaga.
  if (jobQuery.isLoading) {
    return <JobFlowLoadingFallback />;
  }

  // Erro de carregamento (≠ 404, que já redirecionou via useEffect acima).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobFlowErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  return (
    <>
      <JobFlowPageComponent
        fatorHStatus={fatorHStatus}
        jobName={jobName}
        companyLogoUrl={companyLogoUrl}
        companyName={companyName}
        phases={phases}
        readOnly={readOnly}
        isSaving={putJobSteps.isPending}
        onAddPhase={handleAddPhase}
        onPhaseAction={handlePhaseAction}
        onAddStage={handleAddStage}
        onStageAction={handleStageAction}
        onBack={() => {
          void handleBack();
        }}
        onReorder={handleReorder}
        onStageDrop={handleStageDrop}
        isSheetOpen={isSheetOpen}
        onSheetOpenChange={handleSheetOpenChange}
        sheetValues={sheetValues}
        sheetErrors={sheetErrors}
        // MAP-086 — também em loading enquanto a etapa nova grava, ou enquanto
        // um auto-save anterior está em voo (dois PUTs da mesma árvore ao mesmo
        // tempo podem reinserir o que o outro ainda não mapeou — EU-675).
        sheetIsSubmitting={
          (sheetValues.contentUploading ?? false) || addingStage || putJobSteps.isPending
        }
        sheetFormOptions={formOptions}
        sheetTestOptions={testOptions}
        sheetTestsLoading={testsCatalogueQuery.isLoading}
        sheetTestsError={testsCatalogueQuery.isError}
        sheetFatorHOptions={fatorHOptions}
        sheetKolabOptions={kolabOptions}
        sheetKolabCatalogueState={kolabCatalogueState}
        onSheetFieldChange={handleSheetFieldChange}
        onSheetContentFileChange={handleSheetContentFileChange}
        onSheetSubmit={handleSheetSubmit}
        sheetMode={editingStageId ? 'edit' : 'add'}
        isAddPhaseDialogOpen={isAddPhaseDialogOpen}
        onAddPhaseDialogOpenChange={handleAddPhaseDialogOpenChange}
        addPhaseValues={addPhaseValues}
        addPhaseErrors={addPhaseErrors}
        addPhaseIsSubmitting={false}
        addPhaseTypeOptions={[...PHASE_TYPE_OPTIONS]}
        onAddPhaseFieldChange={handleAddPhaseFieldChange}
        onAddPhaseSubmit={handleAddPhaseSubmit}
        addPhaseMode={editingPhaseId ? 'edit' : 'add'}
        isDeletePhaseDialogOpen={deletingPhaseId !== null}
        onDeletePhaseDialogOpenChange={handleDeletePhaseDialogOpenChange}
        onConfirmDeletePhase={handleConfirmDeletePhase}
        isStageConfirmOpen={pendingStageAction !== null}
        onStageConfirmOpenChange={handleStageConfirmOpenChange}
        stageConfirmAction={pendingStageAction?.action ?? null}
        stageConfirmStageName={
          pendingStageAction
            ? (phases
                .find((p) => p.id === pendingStageAction.phaseId)
                ?.stages.find((s) => s.id === pendingStageAction.stageId)?.name ?? '')
            : ''
        }
        onConfirmStageAction={handleConfirmStageAction}
        isReopenDialogOpen={pendingStageEdit !== null || reopenRecovery !== null}
        onReopenDialogOpenChange={handleReopenDialogOpenChange}
        onReopenDecision={handleStageReopenDecision}
        sheetResubmitLock={resubmitLock}
        // Etapa editada com respostas → trava Tipo + catálogo no sheet (defesa
        // server-side no Core via 422 `module_type_locked_has_answers`).
        sheetHasAnswers={
          editingStageId != null &&
          (phases.find((p) => p.id === activePhaseId)?.stages.find((s) => s.id === editingStageId)
            ?.answerCount ?? 0) > 0
        }
      />
      <FatorHSyncSheet jobId={jobId} open={fatorHSyncOpen} onOpenChange={setFatorHSyncOpen} />
      <StageDetailsSheet
        jobId={jobId}
        stepId={detailsSheet?.stepId ?? null}
        moduleId={detailsSheet?.moduleId ?? null}
        title={detailsSheet?.title ?? ''}
        nextStepId={detailsSheet?.nextStepId ?? null}
        nextStepTitle={detailsSheet?.nextStepTitle ?? null}
        open={detailsSheet !== null}
        onOpenChange={(o) => {
          if (!o) setDetailsSheet(null);
        }}
      />
    </>
  );
}

function stageBadgeFromType(type: string): StageBadgeType {
  if (type === 'form') return 'form';
  if (type === 'teste') return 'teste';
  if (type === 'teste-externo') return 'teste-externo';
  if (type === 'fator-h') return 'fator-h';
  if (type === 'conteudo') return 'conteudo';
  if (type === 'case') return 'case';
  if (type === 'curriculo') return 'curriculo';
  if (type === 'complementacao-perfil') return 'complementacao-perfil';
  return 'conteudo';
}
